// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/commaSeparatedEmails';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { DefaultActionSkeleton } from '../../components/dropdowns/DefaultAction';

import { green } from '@mui/material/colors';
import { FieldArray } from 'formik';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
}));

export interface IBusinessSMSTeamsContent {
  teamid: string;
  teamname?: string;
  teamName?: string;
  memberCount: string;
  lastUpdated?: string;
  teamId?: string;
  action?: string;
}

/**
 * Queue teams table and member add
 *
 * @param {Object} props
 * @param {Object} props.id Queue ID
 *
 * @see  https://stackoverflow.com/questions/48901016/how-to-set-change-field-value-from-external-user-action-react-final-form final-form mutators
 */
export const BusinessSMSTeams = ({ id, formik }: any) => {
  const classes = useStyles();
  const [autocomplete, setAutocomplete] = useState<any>([]);
  const businessTeams = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_TEAMS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_TEAMS,
      }),
    })
  );
  // get businessTeams that do not belong to queue
  const teamsNotInBusinessSMSRule = useRef<any>([]);
  teamsNotInBusinessSMSRule.current = useMemo(
    () =>
      businessTeams.state === 'loading'
        ? teamsNotInBusinessSMSRule.current
        : businessTeams.contents?.response?.data?.result
            ?.filter(
              (businessTeam: any) =>
                !formik.values.teams?.find(
                  (team: IBusinessSMSTeamsContent) =>
                    team.teamid === businessTeam.teamid
                )
            )
            ?.sort((a: any, b: any) =>
              a?.teamname
                ?.toLowerCase()
                ?.localeCompare(b?.teamname?.toLowerCase())
            )
            .map((elem) => {
              return {
                ...elem,
                teamId: elem?.teamid,
                teamName: elem?.teamname,
              };
            }) ?? [],
    [businessTeams, formik.values.teams]
  );

  /**
   * Change field with new value
   * @param name Name of field (if nested then last value is type)
   */
  const handleChangeForAutocomplete = (
    event: ChangeEvent<unknown>,
    newValue: any[]
  ) => {
    // keep autocomplete in sync
    setAutocomplete(newValue);
  };

  return (
    <>
      {/* alignItems: 'center', // set native height for button since flex sets height to be the same for all items */}
      <MaterialAutocomplete
        dataPup={'businessSMSTeamsAutocomplete'}
        className={`flex-grow-1 ${classes.field}`}
        type="text"
        label="Teams"
        size="small"
        variant="outlined"
        options={teamsNotInBusinessSMSRule.current}
        value={autocomplete}
        multiple={true}
        onChange={handleChangeForAutocomplete}
        getOptionLabel={(option) => option.teamname ?? ''}
        isOptionEqualToValue={(option, value) =>
          option?.teamid === value?.teamid
        }
        disabled={businessTeams.state === 'loading'} // in progress or queue loading
      />
      <FieldArray
        name="teams"
        render={(arrayHelper) => (
          <>
            <Button
              data-pup={'add-business-sms-team-button'}
              className={classes.field}
              variant="contained"
              color="primary"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                autocomplete.forEach((item: IBusinessSMSTeamsContent) =>
                  arrayHelper.push(item)
                );
                setAutocomplete([]);
              }}
              disableElevation
            >
              Add
            </Button>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell valign="baseline">Team</TableCell>
                  <TableCell valign="baseline">Count</TableCell>
                  <TableCell align="right" valign="baseline" width="100">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(businessTeams.state === 'loading' ||
                  !formik.values.teams) && (
                  <TableRow>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                  </TableRow>
                )}
                {formik.values.teams?.map(
                  (team: IBusinessSMSTeamsContent, index: number) => (
                    <TableRow key={team.teamId}>
                      <TableCell
                        data-pup={`team-name-${index}`}
                        valign="baseline"
                      >
                        {team.teamname ?? team.teamName}
                      </TableCell>
                      <TableCell valign="baseline">
                        {team.memberCount}
                      </TableCell>
                      <TableCell align="right" valign="baseline" padding="none">
                        <IconButton
                          data-pup={`delete-business-sms-team-button-${index}`}
                          aria-label="delete"
                          onClick={(event) => {
                            arrayHelper.remove(index);
                          }}
                          className={classes.deleteIconButton}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </>
        )}
      />
    </>
  );
};
